/**
 * forPageContent.js
 *
 * Section copy for the `/for/:slug` profession pages and the
 * `/for/clinic-management` pillar page, rebuilt on the `/start` design.
 * Pure data, Hebrew only. Per-profession copy stays in landingPagesConfig.js.
 * `{profession}` / `{short}` are replaced at render time.
 */

export const FOR_BREADCRUMB = { home: "דף הבית" };

export const FOR_HERO = {
  overline: "CliniKit ל{profession}",
};

export const FOR_SPECS = {
  overline: "מותאם לתחום שלכם",
  title: "תחומי ההתמחות ב{profession}",
  sub: "CliniKit תומכת בכל תחומי ההתמחות — עם תבניות תיעוד, מעקב התקדמות וכלים שמתאימים לדרך העבודה שלכם.",
};

export const FOR_FEATURES = {
  overline: "נבנה בשבילכם",
  title: "הכלים ש{short} צריכים",
};

export const FOR_TESTIMONIALS = {
  overline: "מטפלים מספרים",
  title: "מה אומרים {short} שכבר עובדים עם CliniKit",
};

export const FOR_PROFESSIONS = {
  overline: "לא רק {profession}",
  title: "CliniKit לכל תחום טיפול",
  sub: "בחרו תחום וגלו איך המערכת מותאמת בדיוק לדרך העבודה שלכם.",
};

/** /for/clinic-management (pillar page) copy that is not per-profession. */
export const CM = {
  seo: {
    title: "תוכנה לניהול קליניקה | CliniKit – מערכת ניהול קליניקה למטפלים בישראל",
    description:
      "תוכנה לניהול קליניקה בעברית למטפלים בישראל. יומן חכם, תיק מטופל, סיכומי טיפול עם AI, תזכורות SMS ווואטסאפ, חשבוניות, סליקה וטפסים דיגיטליים — הכל במקום אחד. מתאימה גם כמערכת לניהול מרפאה. 30 ימי ניסיון חינם.",
    path: "/for/clinic-management",
    breadcrumb: "תוכנה לניהול קליניקה",
    jsonLdDescription:
      "תוכנה לניהול קליניקה בעברית למטפלים בישראל – יומן, תיק מטופל, סיכומי AI, תזכורות SMS, חשבוניות וטפסים דיגיטליים.",
  },
  hero: {
    overline: "תוכנה לניהול קליניקה · בעברית · למטפלים",
    title: "תוכנה לניהול קליניקה. שנבנתה למטפלים בישראל.",
    sub: "CliniKit היא מערכת לניהול קליניקה בעברית שמרכזת את כל מה שצריך — יומן, מטופלים, סיכומי טיפול עם AI, תזכורות, חשבוניות וטפסים — במקום אחד. פחות בירוקרטיה, יותר זמן לטיפול.",
  },
  whatIs: {
    title: "מהי תוכנה לניהול קליניקה?",
    paragraphs: [
      "תוכנה לניהול קליניקה היא מערכת דיגיטלית שמחליפה את האקסל, היומן הידני ופנקס החשבוניות בכלי אחד מסודר. במקום לנהל את הקליניקה בכמה מקומות שונים, כל המידע — מטופלים, פגישות, תיעוד טיפולי, תשלומים וטפסים — מרוכז במערכת אחת מאובטחת שזמינה מכל מכשיר.",
      "עבור מטפלים בקליניקה פרטית, מערכת לניהול קליניקה חוסכת שעות של עבודה אדמיניסטרטיבית בשבוע, מפחיתה ביטולים והיעדרויות בעזרת תזכורות אוטומטיות, ומבטיחה שהתיעוד הרפואי מסודר, מגובה ועומד בדרישות שמירת המידע בישראל.",
    ],
  },
  features: {
    overline: "מה בפנים",
    title: "כל מה שצריך לניהול קליניקה — במקום אחד",
    sub: "CliniKit מחליפה חמישה כלים שונים במערכת אחת שעובדת בשבילכם.",
    items: [
      { icon: "Calendar", title: "יומן ופגישות חכם", desc: "ניהול יומן עם סנכרון Google Calendar, חדרי טיפול ותצוגות יומיות/שבועיות." },
      { icon: "Bell", title: "תזכורות ואישורי הגעה", desc: "תזכורות אוטומטיות ב-WhatsApp ו-SMS ואישורי הגעה בלחיצה — פחות ביטולים והברזות." },
      { icon: "Robot", title: "סיכומי טיפול עם AI", desc: "סיכומי מפגש אוטומטיים והתייעצות בינה מלאכותית בזמן אמת — חיסכון בזמן תיעוד." },
      { icon: "UsersThree", title: "תיק מטופל דיגיטלי", desc: "כל ההיסטוריה, המסמכים והטפסים של המטופל מרוכזים במקום אחד ומאובטחים." },
      { icon: "Receipt", title: "חשבוניות וגבייה", desc: "קישור תשלום, חשבונית אוטומטית דרך Morning, מעקב תשלומים וניהול פיננסי מלא." },
      { icon: "Files", title: "טפסים וחתימה דיגיטלית", desc: "שליחת טפסי הסכמה וחתימה דיגיטלית מהנייד של המטופל — ללא ניירת." },
      { icon: "ChartLineUp", title: "ניהול לידים והכנסות", desc: "מעקב אחר פניות, המרה למטופלים ותמונת הכנסות עדכנית של הקליניקה." },
      { icon: "ShieldCheck", title: "אבטחת מידע רפואי", desc: "הצפנה, יומן פעילות ועמידה בדרישות שמירת מידע רפואי בישראל." },
    ],
  },
  professions: {
    overline: "לפי תחום",
    title: "תוכנה לניהול קליניקה לכל תחום טיפול",
    sub: "בחרו את התחום שלכם וגלו איך CliniKit מותאמת בדיוק לדרך העבודה שלכם.",
  },
  faq: [
    {
      question: "מהי תוכנה לניהול קליניקה?",
      answer:
        "תוכנה לניהול קליניקה היא מערכת דיגיטלית שמרכזת את כל הניהול של קליניקה טיפולית במקום אחד — יומן ופגישות, תיק מטופל, סיכומי טיפול, תזכורות, חשבוניות, טפסים וניהול פיננסי. CliniKit היא מערכת לניהול קליניקה בעברית שנבנתה במיוחד למטפלים בישראל.",
    },
    {
      question: "כמה עולה תוכנה לניהול קליניקה?",
      answer:
        "CliniKit מתחילה ב-39.90 ₪ לחודש למסלול מתחילים ו-89.90 ₪ לחודש למשתמש במסלול המקצועי עם גישה מלאה לכל הכלים, ללא הגבלת מטופלים. מתחילים ב-30 ימי ניסיון חינם ללא כרטיס אשראי.",
    },
    {
      question: "האם המערכת מתאימה לכל סוגי המטפלים?",
      answer:
        "כן. CliniKit מתאימה לקלינאות תקשורת, ריפוי בעיסוק, פיזיותרפיה, פסיכולוגיה, טיפול רגשי, עבודה סוציאלית, תזונה, אימון אישי, רפואה משלימה ועוד — עם תבניות ותכונות מותאמות לכל תחום.",
    },
    {
      question: "האם צריך להתקין משהו?",
      answer:
        "לא. CliniKit היא מערכת מבוססת ענן שעובדת מכל דפדפן ומכל מכשיר — מחשב, טאבלט או טלפון. הנתונים מגובים ומאובטחים אוטומטית.",
    },
    {
      question: "האם אפשר לעבור מאקסל או ממערכת אחרת?",
      answer:
        "כן. אפשר להתחיל לעבוד מיד ולהזין מטופלים בהדרגה. המעבר מאקסל פשוט, והמערכת מסודרת כך שכל המידע מרוכז וזמין במקום אחד.",
    },
  ],
};

export const fill = (template, pageData) =>
  (template || "")
    .replace("{profession}", pageData?.profession || "")
    .replace("{short}", pageData?.professionShort || "");
